import { cpuWaterfall, type CpuWaterfall } from './cpuWaterfall';
import { processFlows, processValue, type ProcessSnapshot } from './processes';

export type WaterfallResource = 'cpu' | 'ram' | 'gpu';

export const WATERFALL_RESOURCES = {
  cpu: { label: 'CPU', device: 'PROCESSOR', chip: 'CPU', total: 'TOTAL CPU TIME', used: 'BUSY TIME', free: 'UNUSED',
    share: 'busy time', totalShare: 'total CPU time', accent: '#6cddcf', light: '#b4f4f3', middle: '#64cdd5', dark: '#3d9dac',
    basis: 'CPU busy time across all logical processors. Not frequency-adjusted performance.' },
  ram: { label: 'RAM', device: 'SYSTEM MEMORY', chip: 'RAM', total: 'USABLE RAM', used: 'IN USE', free: 'AVAILABLE',
    share: 'in-use RAM', totalShare: 'usable RAM', accent: '#be9cfa', light: '#e7d6ff', middle: '#b183e6', dark: '#7353b8',
    basis: 'Private resident application memory. The remainder includes kernel, shared and unassigned memory. Available RAM includes reclaimable pages.' },
  gpu: { label: 'GPU memory', device: 'GRAPHICS MEMORY', chip: 'GPU', total: 'DEDICATED GPU MEMORY', used: 'IN USE', free: 'AVAILABLE',
    share: 'in-use VRAM', totalShare: 'dedicated GPU memory', accent: '#f1c568', light: '#fff0bd', middle: '#e9b64f', dark: '#a57526',
    basis: 'Dedicated memory on one adapter, not GPU engine utilization. Shared system memory is excluded. Shared allocations may require proportional attribution.' },
} as const;

export interface ResourceWaterfallData {
  allocation: CpuWaterfall | null;
  capacity: number | null;
  used: number | null;
  adapter: string | null;
  unavailable: string;
}

export function resourceWaterfall(snapshot: ProcessSnapshot | null, resource: WaterfallResource): ResourceWaterfallData {
  if (resource === 'cpu') return {
    allocation: cpuWaterfall(snapshot?.cpuBusyPercent ?? null, processFlows(snapshot?.processes ?? [], 'cpuPercent', 4)),
    capacity: 100, used: snapshot?.cpuBusyPercent ?? null, adapter: null,
    unavailable: 'Waiting for CPU activity counters.',
  };
  const gpu = resource === 'gpu';
  const capacity = (gpu ? snapshot?.gpuMemoryTotalMb : snapshot?.memoryTotalMb) ?? null;
  const used = (gpu ? snapshot?.gpuMemoryUsedMb : snapshot?.memoryUsedMb) ?? null;
  const ready = capacity !== null && used !== null && Number.isFinite(capacity) && Number.isFinite(used)
    && capacity > 0 && used >= 0 && used <= capacity;
  const allocation = ready ? cpuWaterfall(used / capacity * 100,
    processFlows(snapshot?.processes ?? [], gpu ? 'vramMb' : 'memoryMb', 4)
      .map(flow => ({ ...flow, value: flow.value / capacity * 100 }))) : null;
  if (allocation) allocation.channels = allocation.channels.map(channel => channel.unattributed
    ? { ...channel, name: gpu ? 'Driver / unassigned' : 'System / shared / unassigned' } : channel);
  return { allocation, capacity, used, adapter: gpu ? snapshot?.gpuAdapter ?? null : null,
    unavailable: gpu ? 'Selected-adapter GPU memory is unavailable. An updated recorder and supported graphics counters are required.' : 'System memory capacity is unavailable.' };
}

export function waterfallAmount(percent: number, resource: WaterfallResource, capacity: number | null): string {
  return resource === 'cpu' ? `${percent.toFixed(1)}%`
    : capacity === null ? '\u2014' : processValue(percent / 100 * capacity, resource === 'ram' ? 'memoryMb' : 'vramMb');
}